import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Col, Row, Spin } from 'antd';
import { useTheme } from 'styled-components';
import { useDeepCompareEffect } from 'use-deep-compare';
import { useDispatch, useSelector } from 'react-redux';
import { noop } from 'lodash-es';
import { useMemo, useState } from 'react';
import * as machineDetailsPanelSelectors from '../../../../state/machineDetailsPanelSelectors';
import { MachineRobotCleaningStatus } from './MachineRobotCleaningStatus/MachineRobotCleaningStatus';
import { MachineRobotBasicModeActions } from './MachineRobotBasicModeActions/MachineRobotBasicModeActions';
import { MachineRobotCleaningControls } from './MachineRobotCleaningControls/MachineRobotCleaningControls';
import { StyledMachineRobotStatus } from './MachineRobotStatus.styles';
import { MachineRobotStatusPowerInfo } from './MachineRobotPowerInfo/MachineRobotPowerInfo';
import { StyledMachineRobotPowerInfo } from './MachineRobotPowerInfo/MachineRobotPowerInfo.styles';
import { RobotViewModel } from 'app/modules/cleaning/widgets/robot-dashboard/RobotViewModel';
import { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';
import { ThemeConstants } from 'config/theme/appTheme/appTheme';
import { MachineDetailsPanelRobotActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/machineDetailsPanelRobotSlice';
import { selectCustomerId } from 'app/cross-cutting-concerns/authentication/state/authenticationSelectors';
import { RobotBatteryPercentIcon } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotBatteryIcon/RobotBatteryIcon';
import { RobotWaterLevelIcon } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotWaterLevel/WaterLevelIcon';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { RobotDocking } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotDocking/RobotDocking';
import {
  MachineConnectionStatus,
  RobotStatus,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { RobotRoutine } from 'app/components/RobotRoutine/RobotRoutine';
import { selectRobotCleaningControlIsPaused } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/robotCleaningControlSelectors';
import { RobotUtils } from 'app/utils/robot/RobotUtils';
import { ConvertUnitUtils } from 'app/utils/convert-unit/ConvertUnitUtils';
import { RobotMapName } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotMapName/RobotMapName';
import { RobotCleaningProgress } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotCleaningProgress/RobotCleaningProgress';
import { RobotCleaningRoute } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotCleaningRoute/RobotCleaningRoute';
import { useRobotCleaningProgressEntry } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/hooks/useRobotCleaningProgressEntry';
import { FeatureFlagSelectors } from 'app/cross-cutting-concerns/feature-flags/state/featureFlagSelectors';

interface MachineRobotStatusProps {
  robot: Machine;
}

export const MachineRobotStatus = ({ robot }: MachineRobotStatusProps): React.JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const progressColors = {
    completed: theme.kcc.colors.SUCCESS,
    current: theme.kcc.colors.INFO,
    remaining: theme.kcc.colors.FILL_PROGRESS,
  };

  const robotModelView = new RobotViewModel(robot);

  const customerId = useSelector(selectCustomerId);
  const isTelemetryLoading = useSelector(machineDetailsPanelSelectors.selectIsTelemetryLoading);
  const isLatestCtrLoading = useSelector(machineDetailsPanelSelectors.selectIsLatestCtrLoading);
  const features = useSelector(FeatureFlagSelectors.selectFeatureFlagConfig);
  const isCleaningControlEnabled = features.ROBOT_CLEANING_CONTROL;
  const isPaused = useSelector(selectRobotCleaningControlIsPaused);
  const isLoading = !!isTelemetryLoading || !!isLatestCtrLoading;

  const isDocked = robotModelView.getDocked;
  const isCharging = useMemo(() => robotModelView.getCharging, [robotModelView.getCharging]);
  const isUSusers = false;

  const waterLevel = isUSusers
    ? ConvertUnitUtils.convertLitersToGallons(Number(robotModelView.getFreshWaterLevel))
    : robotModelView.getFreshWaterLevel;

  const waterUnit = isUSusers ? 'gal' : '&#76;';
  const mapName = useMemo(() => RobotUtils.getRobotMapName(robot, t), [robot, t]);
  // getRobotMapName falls back to "Basic Mode" (a status, not a name) when there's no `map` state.
  const hasMapName = !!robot?.states?.find(state => state.stateName === 'map')?.stateValue;
  const isB50 = RobotUtils.getRobotType(robot?.type?.name) === 'B50';
  const isCV50 = RobotUtils.getRobotType(robot?.type?.name) === 'CV50';
  // getRobotType calls every unrecognised robot (a B200 for one) a B50, so the controls need their own check.
  const isCleaningControlSupported = RobotUtils.isCleaningControlSupported(robot?.type?.name);
  // CV50 "Clean now": no map or route.
  const isBasicMode = !isB50 && !hasMapName;

  // Per FR-028, cleaning controls must be hidden for offline robots even if the last-known
  // robotStatus was Autonomous — connectionStatus must be checked too, not just robotStatus.
  const isCleaningMode =
    robot.robotStatus === RobotStatus.Autonomous && robot.connectionStatus === MachineConnectionStatus.Online;
  const [isLiveMapOpen, setIsLiveMapOpen] = useState(false);
  // The live map is hosted by the cleaning controls. The robot stops reporting Autonomous when it heads home near the
  // end of a run, which would unmount them and close the live map on the operator. An open live map keeps them
  // mounted (while the robot is online) until the operator closes it.
  const showCleaningControls =
    isCleaningMode || (isLiveMapOpen && robot.connectionStatus === MachineConnectionStatus.Online);

  // B50 live map title: the routine name while cleaning a routine (a routine run reports its route count),
  // otherwise the name of the route being cleaned. The live map only opens while the robot is cleaning.
  const currentTask = useRobotCleaningProgressEntry(robot, isCleaningControlSupported && isB50 && isCleaningMode);
  const isRoutineCleaning = (currentTask?.stepCount ?? 0) > 0;
  const b50ScheduleName = isRoutineCleaning
    ? currentTask?.subName || (t('robotDashboard.cleaningRoute.label.unNamedRoutine') as string)
    : currentTask?.name ?? '';

  useDeepCompareEffect(() => {
    if (robot.id && customerId) {
      dispatch(
        MachineDetailsPanelRobotActions.subscribeToMachineUpdate({
          customerId,
          machineId: robot.id,
        })
      );

      return (): void => {
        dispatch(
          MachineDetailsPanelRobotActions.unsubscribeFromMachineUpdate({
            customerId,
            machineId: robot.id,
          })
        );
      };
    }

    // No cleanup necessary as long as no subscriptions have been registered
    return noop;
  }, [dispatch]);

  // Same battery icon/layout for both models; only docking indicator and battery count/source
  // differ (B50: one battery + water; CV50: two packs, no water).
  const powerRow = (
    <Row className="machine-robot-status__power-wrapper" justify="start">
      {isDocked &&
        (isB50 ? (
          <Col className="machine-robot-status__docking">
            <StyledMachineRobotPowerInfo>
              <div className="machine-robot-power__wrapper">
                <div className="machine-robot-power__icon">
                  <SvgIcon name="house" size={24} />
                </div>
              </div>
              <span className="machine-robot-power__label">{t('robotDashboard.Docking')}</span>
            </StyledMachineRobotPowerInfo>
          </Col>
        ) : (
          <Col>
            <RobotDocking />
          </Col>
        ))}
      {isB50 ? (
        <>
          <Col>
            <MachineRobotStatusPowerInfo
              icon={
                <RobotBatteryPercentIcon
                  batteryPercentageNumber={Number(robotModelView.getBatteryChargeLevel.toString())}
                  isCharging={isCharging}
                  size={24}
                />
              }
              value={robotModelView.getBatteryChargeLevel.toString()}
              label="robotDashboard.battery"
              unit={'&#37;'}
            />
          </Col>
          <Col>
            <MachineRobotStatusPowerInfo
              icon={<RobotWaterLevelIcon waterPercent={robotModelView.getWaterPercentage} />}
              value={waterLevel.toString()}
              label="robotDashboard.waterLevel"
              unit={waterUnit}
            />
          </Col>
        </>
      ) : (
        <>
          <Col>
            <MachineRobotStatusPowerInfo
              icon={
                <RobotBatteryPercentIcon
                  batteryPercentageNumber={Number(
                    robotModelView.getBatteryDataCV50.batteryPack1.stateOfCharge.toString()
                  )}
                  isCharging={isCharging}
                  size={24}
                />
              }
              value={robotModelView.getBatteryDataCV50.batteryPack1.stateOfCharge.toString()}
              label="robotDashboard.battery"
              unit={'&#37;'}
            />
          </Col>
          <Col>
            <MachineRobotStatusPowerInfo
              icon={
                <RobotBatteryPercentIcon
                  batteryPercentageNumber={Number(
                    robotModelView.getBatteryDataCV50.batteryPack2.stateOfCharge.toString()
                  )}
                  isCharging={isCharging}
                  size={24}
                />
              }
              value={robotModelView.getBatteryDataCV50.batteryPack2.stateOfCharge.toString()}
              label="robotDashboard.battery"
              unit={'&#37;'}
            />
          </Col>
        </>
      )}
    </Row>
  );

  return (
    <StyledMachineRobotStatus
      className={classnames('machine-robot-status', { 'machine-robot-status--loading': isLoading })}
      title={<h3 tabIndex={0}>{t('machineDetails.generalInfo.robotStatus')}</h3>}
    >
      <Spin spinning={isLoading}>
        <Row gutter={[0, ThemeConstants.BASE_GAP]} className="machine-robot-status__card-body">
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={24}
            className="machine-robot-status__info machine-robot-status__info-wrapper"
          >
            <div className="machine-robot-status__cleaning-status">
              <MachineRobotCleaningStatus
                robotStatus={robot.robotStatus}
                connectionStatus={robot.connectionStatus}
                isPaused={isPaused}
              />
            </div>
            {/* RobotRoutine renders nothing without a routine; an empty row would still add a flex gap. */}
            {isB50 && isCleaningMode && robot.latestRoutine?.data && (
              <Row className="machine-robot-status__routine-wrapper">
                <RobotRoutine robot={robot} showCountInHeader progressColors={progressColors} />
              </Row>
            )}
            {!isBasicMode && (
              <div className="machine-robot-status__route-group">
                <Row className="machine-robot-status__cleaning-router-wrapper">
                  <RobotCleaningRoute robot={robot} showProgressPercent robotClassification={isB50 ? 'B50' : 'CV50'} />
                </Row>
                <Row className="machine-robot-status__progress-wrapper">
                  <RobotCleaningProgress robot={robot} showInfo={false} />
                </Row>
              </div>
            )}
            {powerRow}
            {isBasicMode && (
              // Basic mode: no map, so no progress.
              <div className="machine-robot-status__cleaning-map-name">
                <RobotMapName mapName={mapName} robot={robot} hideIcon />
              </div>
            )}
            {/* Cleaning controls apply to both supported models — Skip route UI is wired through
            MachineRobotCleaningControls; backend command wiring is still a noop for now
            (see spec FR-025/FR-026). */}
            {isBasicMode && isCV50 && isCleaningControlSupported && isCleaningMode && isCleaningControlEnabled && (
              <Row className="machine-robot-status__controls-wrapper">
                <MachineRobotBasicModeActions materialNumber={robot.materialNumber} serialNumber={robot.serialNumber} />
              </Row>
            )}
            {!isBasicMode && isCleaningControlSupported && showCleaningControls && isCleaningControlEnabled && (
              <Row className="machine-robot-status__controls-wrapper">
                <MachineRobotCleaningControls
                  isLiveMapOpen={isLiveMapOpen}
                  onLiveMap={(): void => setIsLiveMapOpen(open => !open)}
                  model={isB50 ? 'B50' : 'CV50'}
                  robotName={robot.name}
                  scheduleName={isB50 ? b50ScheduleName : mapName}
                  mapName={mapName}
                  deviceId={robot.id}
                  materialNumber={robot.materialNumber}
                  serialNumber={robot.serialNumber}
                  routeId={robotModelView.getRepeatPathProgressData.repeatPathId}
                  robot={robot}
                />
              </Row>
            )}
          </Col>
        </Row>
      </Spin>
    </StyledMachineRobotStatus>
  );
};
